/**
 * Best Practices & Guides Documentation
 */
import type { DocPage } from '../types';

export const guidesDocs: DocPage[] = [
  {
    id: 'guides/trading-modes',
    title: 'Trading Modes',
    category: 'guides',
    tags: ['trading', 'modes', 'long', 'short', 'bidirectional', 'direction', 'short selling', 'short-only', 'long-only'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/close', 'api/position/current', 'examples/tdr-strategy'],
    order: 1,
    description: 'How to implement Long-only, Short-only, and Bidirectional trading in your strategies',
    content: `# Trading Modes

Quanthop supports three trading modes that control how your strategy enters and exits positions. Each mode must be **natively implemented** in your strategy code — the engine does not auto-invert or transform signals.

## Overview

| Mode | Description | When to Use |
|------|-------------|-------------|
| **Long-only** | Buy low, sell high | Default. Trend-following, mean-reversion on dips |
| **Short-only** | Sell high, buy back lower | Bearish strategies, hedging |
| **Bidirectional** | Both long and short, always in market | Momentum strategies, regime-switching |

## How It Works

Your strategy receives the selected trading mode via \`ctx.p.tradingMode\`. Based on this value, your \`onBar\` function decides which orders to place. If your strategy only handles \`LONG_ONLY\`, the Short-only and Bidirectional options will be locked in the UI.

## Long-only (Default)

The simplest and most common mode. Your strategy buys with a positive quantity and closes the position to exit. If your strategy does not check \`ctx.p.tradingMode\`, it operates as long-only by default.

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma[i];
  const slow = ctx.ind.slowEma[i];
  const pos = ctx.position('ASSET');

  // Enter long when fast EMA crosses above slow EMA
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }

  // Exit long when fast EMA crosses below slow EMA
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty > 0) {
    ctx.order.close('ASSET', { signal: 'sell' });
  }
}
\`\`\`

**Key points:**
- Quantity of \`1\` means "use full allocation" for a long entry
- \`ctx.order.close()\` exits the entire position
- Position is flat (qty === 0) when not in a trade

## Short-only

Short-only strategies profit when prices fall. You enter a short position with a **negative quantity** (\`-1\`) and close it to exit.

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma[i];
  const slow = ctx.ind.slowEma[i];
  const pos = ctx.position('ASSET');

  // Enter short when fast EMA crosses below slow EMA
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty === 0) {
    ctx.order.market('ASSET', -1, { signal: 'enterShort' });
  }

  // Exit short when fast EMA crosses above slow EMA
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty < 0) {
    ctx.order.close('ASSET', { signal: 'exitShort' });
  }
}
\`\`\`

**Key points:**
- Quantity of \`-1\` opens a short position (sell first, buy back later)
- \`pos.qty < 0\` indicates an open short position
- \`ctx.order.close()\` buys back to close the short
- Short P&L is positive when exit price < entry price

## Bidirectional

Bidirectional strategies can hold both long and short positions, flipping between them as the market changes. This mode is for strategies that are always in the market.

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma[i];
  const slow = ctx.ind.slowEma[i];
  const pos = ctx.position('ASSET');

  // Bullish signal: close any short, enter long
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    if (pos.qty < 0) {
      ctx.order.close('ASSET', { signal: 'exitShort' });
    }
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }

  // Bearish signal: close any long, enter short
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    if (pos.qty > 0) {
      ctx.order.close('ASSET', { signal: 'sell' });
    }
    ctx.order.market('ASSET', -1, { signal: 'enterShort' });
  }
}
\`\`\`

**Key points:**
- Always close the opposing position before opening a new one
- Check \`pos.qty > 0\` (long) or \`pos.qty < 0\` (short) to know current state
- The strategy flips from long to short (or vice versa) on each signal
- More trades than long-only, so watch for excessive fees in choppy markets

## Handling Multiple Modes in One Strategy

To support multiple modes, read \`ctx.p.tradingMode\` and branch your logic accordingly. This is the recommended approach for versatile strategies:

\`\`\`javascript
function onBar(ctx, i) {
  const tradingMode = ctx.p.tradingMode || 'LONG_ONLY';
  const pos = ctx.position('ASSET');

  // Compute your signals
  const bullish = q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i);
  const bearish = q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i);

  if (tradingMode === 'LONG_ONLY') {
    if (bullish && pos.qty === 0) {
      ctx.order.market('ASSET', 1, { signal: 'buy' });
    }
    if (bearish && pos.qty > 0) {
      ctx.order.close('ASSET', { signal: 'sell' });
    }
  } else if (tradingMode === 'SHORT_ONLY') {
    if (bearish && pos.qty === 0) {
      ctx.order.market('ASSET', -1, { signal: 'enterShort' });
    }
    if (bullish && pos.qty < 0) {
      ctx.order.close('ASSET', { signal: 'exitShort' });
    }
  } else if (tradingMode === 'BIDIRECTIONAL') {
    if (bullish) {
      if (pos.qty < 0) ctx.order.close('ASSET', { signal: 'exitShort' });
      ctx.order.market('ASSET', 1, { signal: 'buy' });
    }
    if (bearish) {
      if (pos.qty > 0) ctx.order.close('ASSET', { signal: 'sell' });
      ctx.order.market('ASSET', -1, { signal: 'enterShort' });
    }
  }
}
\`\`\`

## Declaring Supported Modes

When you create a strategy from a template, the supported modes are defined in the template metadata. Only modes listed in \`supportedTradingModes\` will be selectable in the Direction dropdown.

\`\`\`javascript
// Template supports all three modes
supportedTradingModes: ['LONG_ONLY', 'SHORT_ONLY', 'BIDIRECTIONAL']

// Template supports long-only (default if omitted)
supportedTradingModes: ['LONG_ONLY']
\`\`\`

If your strategy code only handles long entries — don't declare SHORT_ONLY or BIDIRECTIONAL. The UI will show a lock icon on unsupported modes to prevent misconfiguration.

## Signal Names

Use descriptive signal names so your backtest results are easy to read:

| Action | Signal Name | Order |
|--------|-------------|-------|
| Enter long | \`'buy'\` or \`'enterLong'\` | \`ctx.order.market('ASSET', 1, { signal: 'buy' })\` |
| Exit long | \`'sell'\` or \`'exitLong'\` | \`ctx.order.close('ASSET', { signal: 'sell' })\` |
| Enter short | \`'enterShort'\` or \`'short'\` | \`ctx.order.market('ASSET', -1, { signal: 'enterShort' })\` |
| Exit short | \`'exitShort'\` or \`'coverShort'\` | \`ctx.order.close('ASSET', { signal: 'exitShort' })\` |

## Partial Position Sizing

Both long and short modes support partial positions:

\`\`\`javascript
// Partial long entry (50% of capital)
ctx.order.market('ASSET', 0.5, { signal: 'partialBuy' });

// Partial short entry (50% of capital)
ctx.order.market('ASSET', -0.5, { signal: 'partialShort' });

// Reduce long position by 25%
ctx.order.reduce('ASSET', 0.25, { signal: 'takeProfit' });

// Reduce short position by 25%
ctx.order.reduce('ASSET', 0.25, { signal: 'partialCover' });
\`\`\`

## Checking Position State

Use \`ctx.position()\` to check where you are before placing orders:

\`\`\`javascript
const pos = ctx.position('ASSET');

pos.qty === 0   // Flat (no position)
pos.qty > 0     // Long position open
pos.qty < 0     // Short position open
pos.avgPrice    // Average entry price
pos.unrealized  // Current P&L (unrealized)
\`\`\`

## Common Mistakes

**1. Forgetting to close before flipping (bidirectional)**
\`\`\`javascript
// Wrong: opens short while still long
ctx.order.market('ASSET', -1, { signal: 'enterShort' });

// Correct: close long first, then enter short
if (pos.qty > 0) ctx.order.close('ASSET', { signal: 'sell' });
ctx.order.market('ASSET', -1, { signal: 'enterShort' });
\`\`\`

**2. Using positive quantity for shorts**
\`\`\`javascript
// Wrong: this opens a long position
ctx.order.market('ASSET', 1, { signal: 'enterShort' });

// Correct: use negative quantity
ctx.order.market('ASSET', -1, { signal: 'enterShort' });
\`\`\`

**3. Not checking current position before entry**
\`\`\`javascript
// Wrong: may stack multiple entries
if (bullish) ctx.order.market('ASSET', 1, { signal: 'buy' });

// Correct: only enter when flat
if (bullish && pos.qty === 0) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}
\`\`\`
`,
  },

  // Position Sizing
  {
    id: 'guides/position-sizing',
    title: 'Position Sizing',
    category: 'guides',
    tags: ['position', 'sizing', 'allocation', 'risk', 'capital', 'quantity'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/reduce', 'api/position/current', 'guides/risk-management'],
    order: 2,
    description: 'How to control position sizes and capital allocation in strategies',
    content: `# Position Sizing

Position sizing determines how much capital to allocate to each trade. Getting this right is often more important than entry timing.

## How Quantity Works

The \`quantity\` parameter in \`ctx.order.market()\` represents a fraction of your total capital:

| Quantity | Meaning |
|----------|---------|
| \`1\` | 100% of capital (full position) |
| \`0.5\` | 50% of capital |
| \`0.25\` | 25% of capital |
| \`-1\` | Full short position |
| \`-0.5\` | 50% short position |

\`\`\`javascript
// Full allocation
ctx.order.market('ASSET', 1, { signal: 'buy' });

// Half allocation
ctx.order.market('ASSET', 0.5, { signal: 'buy' });

// Quarter allocation
ctx.order.market('ASSET', 0.25, { signal: 'buy' });
\`\`\`

## Fixed Fractional Sizing

The simplest approach: risk a fixed percentage of capital on every trade.

\`\`\`javascript
function init(ctx) {
  ctx.addIndicator('atr', 'ATR', { period: 14 });
}

function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  if (pos.qty !== 0) return;

  const close = ctx.series.close[i];
  const atr = ctx.ind.atr[i];
  if (q.isNaN(atr)) return;

  // Risk 2% of capital per trade
  // Size inversely proportional to volatility
  const riskPerTrade = 0.02;
  const stopDistance = atr * 2;
  const rawSize = (riskPerTrade * close) / stopDistance;
  const size = q.clamp(rawSize, 0.1, 1);

  if (q.crossOver(ctx.ind.ema, ctx.series.close, i)) {
    ctx.order.market('ASSET', size, { signal: 'buy' });
  }
}
\`\`\`

## Volatility-Adjusted Sizing

Scale position size inversely with volatility. When the market is calm, take larger positions; when volatile, take smaller ones.

\`\`\`javascript
function onBar(ctx, i) {
  const atr = ctx.ind.atr[i];
  const avgAtr = q.sma(ctx.ind.atr, 50, i);
  if (q.isNaN(avgAtr) || avgAtr === 0) return;

  // Normalize: if current ATR = average, size = 1
  // Higher ATR = smaller size, lower ATR = larger size
  const volRatio = avgAtr / atr;
  const size = q.clamp(volRatio, 0.2, 1);

  if (shouldEnter) {
    ctx.order.market('ASSET', size, { signal: 'buy' });
  }
}
\`\`\`

## Scaling In and Out

Use \`ctx.order.reduce()\` to take partial profits, or multiple entries to scale in.

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const close = ctx.series.close[i];

  // Scale in: first entry at 50%, add 50% on confirmation
  if (pos.qty === 0 && initialSignal) {
    ctx.order.market('ASSET', 0.5, { signal: 'buy_initial' });
  }

  // Add to position on confirmation
  if (pos.qty > 0 && pos.qty < 0.9 && confirmationSignal) {
    ctx.order.market('ASSET', 0.5, { signal: 'buy_add' });
  }

  // Scale out: take 50% profit at first target
  if (pos.qty > 0 && close > pos.avgPrice * 1.05) {
    ctx.order.reduce('ASSET', 0.5, { signal: 'takeProfit1' });
  }

  // Close remainder at second target
  if (pos.qty > 0 && close > pos.avgPrice * 1.10) {
    ctx.order.close('ASSET', { signal: 'takeProfit2' });
  }
}
\`\`\`

## Common Mistakes

**Over-sizing:** Using \`1\` (100%) for every trade leaves no room for drawdowns. Consider 25-50% as a starting point.

**Ignoring volatility:** A fixed size of 0.5 means very different risk levels in calm vs choppy markets. Use ATR to normalize.

**Stacking entries:** Always check \`pos.qty === 0\` before initial entry, or intentionally manage scaling via quantity checks.

## Related

- [ctx.order.market()](/docs/api/orders/market) — Placing orders
- [ctx.order.reduce()](/docs/api/orders/reduce) — Partial exits
- [Risk Management](/docs/guides/risk-management) — Stop-loss patterns
`,
  },

  // Risk Management Patterns
  {
    id: 'guides/risk-management',
    title: 'Risk Management',
    category: 'guides',
    tags: ['risk', 'stop-loss', 'take-profit', 'drawdown', 'risk-reward', 'trailing'],
    version: '2.0',
    related: ['api/orders/close', 'api/orders/reduce', 'api/position/current', 'guides/position-sizing'],
    order: 3,
    description: 'Stop-loss, take-profit, and risk control patterns for QSL strategies',
    content: `# Risk Management

Effective risk management prevents catastrophic losses. QSL strategies implement risk rules directly in \`onBar()\` logic.

## ATR-Based Stop Loss

ATR (Average True Range) adapts your stop to current volatility:

\`\`\`javascript
function init(ctx) {
  ctx.addIndicator('atr', 'ATR', { period: 14 });
  ctx.addIndicator('ema', 'EMA', { period: 20 });
}

function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const close = ctx.series.close[i];
  const atr = ctx.ind.atr[i];
  if (q.isNaN(atr)) return;

  // Entry
  if (pos.qty === 0 && q.crossOver(ctx.series.close, ctx.ind.ema, i)) {
    ctx.order.market('ASSET', 1, {
      signal: 'buy',
      stopPrice: close - atr * 2  // Store stop level in metadata
    });
  }

  // Stop loss: exit if price drops 2x ATR below entry
  if (pos.qty > 0) {
    const stopLevel = pos.avgPrice - atr * 2;
    if (close <= stopLevel) {
      ctx.order.close('ASSET', { signal: 'stopLoss' });
    }
  }
}
\`\`\`

## Percentage-Based Stop and Target

Simple fixed-percentage rules:

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const close = ctx.series.close[i];

  if (pos.qty > 0) {
    const pnlPct = (close - pos.avgPrice) / pos.avgPrice;

    // Stop loss at -3%
    if (pnlPct <= -0.03) {
      ctx.order.close('ASSET', { signal: 'stopLoss' });
      return;
    }

    // Take profit at +6% (2:1 reward-risk)
    if (pnlPct >= 0.06) {
      ctx.order.close('ASSET', { signal: 'takeProfit' });
      return;
    }
  }
}
\`\`\`

## Trailing Stop

Lock in profits as the trade moves in your favor:

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const high = ctx.series.high[i];
  const close = ctx.series.close[i];

  if (pos.qty > 0) {
    // Track the highest price since entry using lookback
    const barsSinceEntry = 20; // Or track via metadata
    const highestSinceEntry = q.highest(ctx.series.high, barsSinceEntry, i);

    // Trail stop 3% below the peak
    const trailingStop = highestSinceEntry * 0.97;

    if (close <= trailingStop) {
      ctx.order.close('ASSET', { signal: 'trailingStop' });
    }
  }
}
\`\`\`

## Tiered Profit Taking

Reduce risk progressively as the trade succeeds:

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  if (pos.qty <= 0) return;

  const close = ctx.series.close[i];
  const gain = (close - pos.avgPrice) / pos.avgPrice;

  // Take 1/3 at +3%
  if (gain >= 0.03 && pos.qty > 0.6) {
    ctx.order.reduce('ASSET', 0.33, { signal: 'tp1' });
  }

  // Take 1/3 at +6%
  if (gain >= 0.06 && pos.qty > 0.3) {
    ctx.order.reduce('ASSET', 0.5, { signal: 'tp2' });
  }

  // Close remainder at +10%
  if (gain >= 0.10) {
    ctx.order.close('ASSET', { signal: 'tp3' });
  }
}
\`\`\`

## Risk-Reward Filtering

Only take trades where reward potential exceeds risk:

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const atr = ctx.ind.atr[i];
  const pos = ctx.position('ASSET');
  if (pos.qty !== 0 || q.isNaN(atr)) return;

  const stopDistance = atr * 2;
  const targetDistance = atr * 4;  // 2:1 R:R ratio

  // Only enter if there's room to run
  const resistance = q.highest(ctx.series.high, 50, i);
  const upside = resistance - close;

  if (upside > stopDistance * 2) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

## Key Principles

1. **Always have an exit plan** — every entry should have a defined stop loss
2. **Size positions relative to stop distance** — wider stops need smaller positions
3. **Use ATR over fixed percentages** — adapts to changing market conditions
4. **Consider fees** — tight stops with frequent trading erode capital through commissions (default 0.1% per side)

## Related

- [Position Sizing](/docs/guides/position-sizing) — Capital allocation
- [Commissions & Fees](/docs/engine/fees) — Fee model details
- [ctx.order.reduce()](/docs/api/orders/reduce) — Partial exits
`,
  },

  // Warm-up Periods
  {
    id: 'guides/warmup-periods',
    title: 'Warm-up Periods',
    category: 'guides',
    tags: ['warmup', 'warm-up', 'indicator', 'NaN', 'bars', 'period', 'initialization'],
    version: '2.0',
    related: ['api/lifecycle/init', 'api/helpers/overview', 'engine/indicators/computation'],
    order: 4,
    description: 'Understanding and configuring indicator warm-up periods',
    content: `# Warm-up Periods

Indicators like EMA, RSI, and ATR need a minimum number of bars before they produce valid output. The warm-up period prevents your strategy from trading on incomplete data.

## The Problem

On the first few bars, indicators return \`NaN\` because they haven't accumulated enough data:

\`\`\`
Bar 0:  EMA(20) = NaN
Bar 1:  EMA(20) = NaN
...
Bar 19: EMA(20) = NaN
Bar 20: EMA(20) = 48523.45  (first valid value)
\`\`\`

If your strategy doesn't handle this, it will make decisions based on invalid data.

## How warmupBars Works

The \`warmupBars\` parameter tells the engine to skip the first N bars of \`onBar()\` execution entirely. Your strategy code simply won't run until bar N.

\`\`\`javascript
// In your strategy parameters
{
  warmupBars: 50  // Skip the first 50 bars
}
\`\`\`

The engine starts the \`onBar\` loop at bar \`warmupBars\` instead of bar 0. This is more efficient than checking \`q.isNaN()\` on every bar — the engine skips the loop iterations entirely.

## Calculating the Right Value

Your warm-up should be at least as long as the longest indicator period, plus a safety margin:

| Indicator | Period | Warm-up Needed |
|-----------|--------|---------------|
| EMA(20) | 20 | 20 bars |
| RSI(14) | 14 | 15 bars |
| BBANDS(20) | 20 | 20 bars |
| MACD(12,26,9) | 26+9 | 35 bars |
| ATR(14) | 14 | 15 bars |

**Rule of thumb:** Take the largest indicator period and add 1.

\`\`\`javascript
// If your strategy uses EMA(50) and RSI(14):
// Warm-up = max(50, 14) + 1 = 51
{
  warmupBars: 51
}
\`\`\`

## Defensive Coding: q.isNaN()

Even with \`warmupBars\` set, it's good practice to guard against NaN values. This protects against edge cases and makes your strategy robust:

\`\`\`javascript
function onBar(ctx, i) {
  const ema = ctx.ind.ema[i];
  const rsi = ctx.ind.rsi[i];

  // Early return if any indicator isn't ready
  if (q.isNaN(ema) || q.isNaN(rsi)) return;

  // Safe to use indicators here
  if (rsi < 30 && q.crossOver(ctx.series.close, ctx.ind.ema, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

## Chained Indicators

When one indicator depends on another's output, warm-up compounds:

\`\`\`javascript
function init(ctx) {
  ctx.addIndicator('ema', 'EMA', { period: 50 });
  ctx.addIndicator('rsi', 'RSI', { period: 14 });
  // If you use EMA of RSI, you'd need 50 + 14 = 64 bars
}
\`\`\`

## Impact on Backtests

- Warm-up bars consume data but **don't generate trades**
- A 200-bar warm-up on 500 bars of data leaves only 300 bars for actual trading
- Shorter timeframes (1h) provide more bars, so warm-up is less impactful
- Longer timeframes (1d) with large warm-ups may leave too few bars — use adequate date ranges

## Common Mistakes

**1. Setting warmupBars too low**
\`\`\`javascript
// Wrong: EMA(200) needs at least 200 bars
{ warmupBars: 50 }

// Correct
{ warmupBars: 201 }
\`\`\`

**2. Not accounting for all indicators**
\`\`\`javascript
// Uses EMA(20), RSI(14), and BBANDS(50)
// warmupBars should be max(20, 14, 50) + 1 = 51
\`\`\`

**3. Forgetting compound indicators**
MACD uses a 26-period EMA and a 9-period signal line, so it needs at least 35 bars.

## Related

- [init()](/docs/api/lifecycle/init) — Declaring indicators
- [q.isNaN()](/docs/api/helpers/overview) — Checking indicator readiness
- [Indicator Computation](/docs/engine/indicators/computation) — How the engine computes indicators
`,
  },

  // Debugging Strategies
  {
    id: 'guides/debugging',
    title: 'Debugging Strategies',
    category: 'guides',
    tags: ['debugging', 'troubleshooting', 'errors', 'no trades', 'NaN', 'common issues'],
    version: '2.0',
    related: ['api/lifecycle/onBar', 'api/helpers/overview', 'guides/warmup-periods'],
    order: 5,
    description: 'Common strategy issues and how to diagnose and fix them',
    content: `# Debugging Strategies

When a backtest produces unexpected results — no trades, wrong signals, or poor metrics — use these patterns to diagnose the issue.

## No Trades Generated

The most common problem. Your strategy runs but places zero orders.

### Check 1: Indicators returning NaN

If your warm-up is too short, indicators may be NaN for the entire run:

\`\`\`javascript
function onBar(ctx, i) {
  // Add temporary logging
  if (i < 5) {
    console.log('Bar', i, 'EMA:', ctx.ind.ema[i], 'RSI:', ctx.ind.rsi[i]);
  }
}
\`\`\`

**Fix:** Increase \`warmupBars\` to at least \`max(all indicator periods) + 1\`.

### Check 2: Conditions never met simultaneously

Multiple filters may be individually reasonable but never true at the same time:

\`\`\`javascript
// These might never all be true at once
if (rsi < 30 && close > ema200 && volume > avgVol * 3 && atr < threshold) {
  // Too many conditions
}
\`\`\`

**Fix:** Test each condition independently. Remove filters one at a time to see which one blocks all entries.

### Check 3: Position check prevents re-entry

\`\`\`javascript
// If you close and re-enter on the same bar, the position
// might not be 0 when you expect it to be
if (pos.qty === 0 && buySignal) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}
\`\`\`

**Fix:** Remember that orders fill on the next bar's open. Position state reflects fills, not pending orders.

## Unexpected Entry/Exit Timing

### Order Fill Timing

All market orders fill at the **next bar's open**, not the current bar's close:

\`\`\`
Bar 100: Signal detected (close = $50,000)
Bar 101: Order fills (open = $50,150)  <-- actual entry price
\`\`\`

This is by design — it prevents look-ahead bias. If you see entries at "wrong" prices, check that you're comparing against the correct bar.

### Crossover Fires Once

\`q.crossOver()\` returns true only on the bar where the crossing happens, not while one series remains above the other:

\`\`\`javascript
// This fires once (correct)
if (q.crossOver(fastEma, slowEma, i)) { ... }

// This fires every bar while fast > slow (different behavior)
if (fastEma[i] > slowEma[i]) { ... }
\`\`\`

## Excessive Trading

If your strategy generates too many trades with high turnover:

### Choppy Signal Filter

Add a minimum bars-between-trades cooldown:

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');

  // Simple cooldown: don't re-enter within 5 bars of an exit
  if (pos.qty === 0 && pos.lastExitBar && i - pos.lastExitBar < 5) {
    return;
  }
}
\`\`\`

### Trend Filter

Only trade in the direction of the larger trend:

\`\`\`javascript
function init(ctx) {
  ctx.addIndicator('fastEma', 'EMA', { period: 10 });
  ctx.addIndicator('slowEma', 'EMA', { period: 50 });
  ctx.addIndicator('trendEma', 'EMA', { period: 200 });
}

function onBar(ctx, i) {
  const inUptrend = ctx.series.close[i] > ctx.ind.trendEma[i];

  // Only take long entries in an uptrend
  if (inUptrend && q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

## Poor Performance Metrics

### High Drawdown

- Position sizing too aggressive — reduce quantity from 1 to 0.5 or less
- No stop loss — add ATR-based or percentage-based stops
- Trading against the trend — add a trend filter

### Low Win Rate

- Entry signals may be noisy — add confirmation filters
- Stops too tight — widen stop distance (e.g. 2x ATR instead of 1x)
- Wrong timeframe — some strategies work better on higher timeframes

### High Fee Drag

- Check total fees in the performance metrics
- Default fee is 0.1% per side (0.2% round trip)
- Tight stop-losses in choppy markets generate many small losing trades where fees dominate

## Strategy Checklist

Before running a backtest, verify:

- [ ] All indicators declared in \`init()\` with correct parameters
- [ ] \`warmupBars\` >= max indicator period + 1
- [ ] \`q.isNaN()\` guards on indicator values
- [ ] Position check (\`pos.qty === 0\`) before entries
- [ ] Both entry AND exit logic defined
- [ ] Signal names are descriptive
- [ ] Quantity is reasonable (0.1 to 1.0)

## Related

- [Warm-up Periods](/docs/guides/warmup-periods) — Indicator initialization
- [Order Fill Timing](/docs/engine/execution/order-timing) — How orders execute
- [Performance Metrics](/docs/engine/performance-metrics) — Understanding results
`,
  },
];
